// frontend/src/pages/TrustApply.tsx
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import {
  apiTrustEligibility,
  apiTrustApply,
  apiTrustDeedPreview,
  apiTrustDeedCommit,
  openPdfBlob,
} from "../lib/api";

type JurisdictionCode = "bahamas" | "wyoming" | "uk";

type Props = {
  mode?: "apply" | "reapply";
};

export default function TrustApply({ mode = "apply" }: Props) {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isReapply = mode === "reapply" && !!id;

  const [loading, setLoading] = useState(false);

  const [trustName, setTrustName] = useState("");
  const [enforcer, setEnforcer] = useState("");
  const [enforcerAddress, setEnforcerAddress] = useState("");
  const [jurisdiction, setJurisdiction] = useState<JurisdictionCode>("bahamas");

  const [acceptDeed, setAcceptDeed] = useState(false);
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elig, setElig] =
    useState<null | Awaited<ReturnType<typeof apiTrustEligibility>>>(null);

  useEffect(() => {
    (async () => {
      try {
        const e = await apiTrustEligibility();
        setElig(e);

        // If user already has pending/active, send them to trusts index.
        if (e.action === "dashboard") nav("/trusts");
        if (e.action === "blocked") setError(e.error || "Blocked");
      } catch {
        // ignore (offline or boot race)
      }
    })();
  }, [nav]);

  const remainingView = useMemo(() => {
    if (!elig || elig.action !== "not-eligible") return null;
    const { eligibility } = elig;
    return (
      <div className="alert-info mb-4">
        Minimum deposit: <b>€{eligibility.minDeposit.toLocaleString()}</b>. You
        have <b>€{eligibility.userDeposited.toLocaleString()}</b>. You need{" "}
        <b>€{eligibility.remaining.toLocaleString()}</b> more to start.
      </div>
    );
  }, [elig]);

  const canPreview = Boolean(trustName && enforcer && jurisdiction && !loading);
  const canSubmit = Boolean(
    trustName &&
      enforcer &&
      enforcerAddress &&
      jurisdiction &&
      agree &&
      acceptDeed &&
      !loading
  );

  const onPreviewDeed = async () => {
    setError(null);
    try {
      const pdf = await apiTrustDeedPreview({
        trust_name: trustName,
        enforcer,
        enforcer_address: enforcerAddress || undefined,
        jurisdiction,
      });
      openPdfBlob(pdf);
    } catch (e: any) {
        setError(e?.message || "Could not preview deed.");
    }
  };

  const onSubmit = async () => {
    setError(null);
    if (!agree) return setError("Please accept the Trust Terms.");
    if (!acceptDeed) return setError("Please confirm you accept the Trust Deed.");
    if (!enforcerAddress.trim()) {
      return setError("Please fill in the Protector / Enforcer address.");
    }

    setLoading(true);
    try {
      // Commit deed + create pending draft
      const commit = await apiTrustDeedCommit({
        trust_name: trustName,
        enforcer,
        enforcer_address: enforcerAddress,
        jurisdiction,
        accept_deed: true,
      });
      if (!commit.ok) throw new Error("Failed to commit deed");

      // Also create application record (if not created)
      const res = await apiTrustApply({
        trust_name: trustName,
        enforcer,
        enforcer_address: enforcerAddress,
        jurisdiction,
        terms: true,
      });

      if (!res.ok && res.requires_reapply && res.trust_id) {
        return nav(`/trusts/${res.trust_id}`);
      }
      return nav("/trusts");
    } catch (e: any) {
      setError(e?.message || "Could not submit application.");
    } finally {
      setLoading(false);
    }
  };

  if (elig && elig.action === "not-eligible") {
    return (
      <div className="section-box max-w-2xl mx-auto p-4 sm:p-6">
        <h1 className="text-xl font-semibold mb-2">Trust – Not Eligible Yet</h1>
        {remainingView}
        <div className="mt-4">
          <Link className="btn" to="/funding">
            Add funds
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="section-box max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-32">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-xl font-semibold">
          {isReapply ? "Reapply for Your Trust" : "Create Your Trust"}
        </h1>
        <p className="text-gray-600 text-sm">
          Provide a name, choose jurisdiction, and add a Protector / Enforcer with
          address. You can preview the deed before accepting.
        </p>
        {isReapply && id && (
          <p className="mt-1 text-xs text-gray-500">
            You are updating your previously rejected trust application (ID #{id}).
          </p>
        )}
      </div>

      {error && <div className="alert-danger mb-4">{error}</div>}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        className="space-y-6"
      >
        {/* Trust name */}
        <div className="form-section space-y-2">
          <label htmlFor="trust_name" className="block text-sm font-medium">
            Trust name
          </label>
          <input
            id="trust_name"
            value={trustName}
            onChange={(e) => setTrustName(e.target.value)}
            placeholder="e.g. Alpha"
            className={[
              "input w-full",
              "rounded-xl border border-gray-300 px-3 py-2",
              "focus:outline-none focus:ring-2 focus:ring-blue-500",
            ].join(" ")}
          />
          <p className="text-xs text-gray-500">
            We’ll prefix with “Trust ” automatically before the name.
          </p>
        </div>

        {/* Jurisdiction selector (Bahamas, UK, Wyoming) */}
        <div className="form-section space-y-2">
          <label htmlFor="jurisdiction" className="block text-sm font-medium">
            Jurisdiction
          </label>
          <select
            id="jurisdiction"
            value={jurisdiction}
            onChange={(e) =>
              setJurisdiction(e.target.value as JurisdictionCode)
            }
            className={[
              "input w-full",
              "rounded-xl border border-gray-300 px-3 py-2 bg-white",
              "focus:outline-none focus:ring-2 focus:ring-blue-500",
            ].join(" ")}
          >
            <option value="bahamas">The Bahamas – Purpose Trust</option>
            <option value="uk">United Kingdom – Discretionary Trust</option>
            <option value="wyoming">Wyoming (USA) – UTC Trust</option>
          </select>
          <p className="text-xs text-gray-500">
            Exact deed text and references are adapted automatically for the
            selected jurisdiction.
          </p>
        </div>

        {/* Enforcer name */}
        <div className="form-section space-y-2">
          <label htmlFor="enforcer" className="block text-sm font-medium">
            Protector / Enforcer – Full name
          </label>
          <input
            id="enforcer"
            value={enforcer}
            onChange={(e) => setEnforcer(e.target.value)}
            placeholder="Full legal name"
            className={[
              "input w-full",
              "rounded-xl border border-gray-300 px-3 py-2",
              "focus:outline-none focus:ring-2 focus:ring-blue-500",
            ].join(" ")}
          />
        </div>

        {/* Enforcer address */}
        <div className="form-section space-y-2">
          <label htmlFor="enforcer_address" className="block text-sm font-medium">
            Protector / Enforcer – Address
          </label>
          <textarea
            id="enforcer_address"
            value={enforcerAddress}
            onChange={(e) => setEnforcerAddress(e.target.value)}
            rows={3}
            placeholder="Street, postal code, city, country"
            className={[
              "input w-full",
              "rounded-xl border border-gray-300 px-3 py-2",
              "focus:outline-none focus:ring-2 focus:ring-blue-500",
              "resize-y",
            ].join(" ")}
          />
          <p className="text-xs text-gray-500">
            This address is included in the deed as the Protector / Enforcer
            address.
          </p>
        </div>

        {/* Terms & deed acceptance */}
        <div className="form-section space-y-3">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              id="agree"
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
            />
            <span className="text-sm">
              I agree to the Trust Terms and acknowledge the risks and scope of
              the structure.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              id="accept_deed"
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={acceptDeed}
              onChange={(e) => setAcceptDeed(e.target.checked)}
            />
            <span className="text-sm">
              I confirm that I have reviewed and accept the Trust Deed for the
              selected jurisdiction.
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
          <button
            type="button"
            className="btn w-full sm:w-auto"
            onClick={onPreviewDeed}
            disabled={!canPreview}
          >
            Preview Deed (PDF)
          </button>
          <button
            type="submit"
            className="btn btn-success w-full sm:w-auto"
            disabled={!canSubmit}
          >
            {loading
              ? isReapply
                ? "Updating…"
                : "Creating…"
              : isReapply
              ? "Reapply"
              : "Create Trust"}
          </button>
          <Link className="btn btn-white w-full sm:w-auto" to="/dashboard">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
